import cx from 'classnames';
import {filter, findIndex, isEmpty} from 'lodash';
import {useState, useMemo, cloneElement, useEffect, useCallback, Children} from 'react';

import {Icon, IconName} from 'components';

type TabProps = {
    name: string;
    hidden?: boolean;
    disabled?: boolean;
    activeItemName?: string;
    icon?: IconName;
    children: React.ReactElement;
}

export const Tab: React.FC<TabProps> = ({name, children, activeItemName}: TabProps) =>
    name === activeItemName ? children : null;

type Props = {
    stretch?: boolean;
    contentIndent?: boolean;
    overflow?: boolean;
    defaultTabName?: string;
    className?: string;
    children?: React.ReactElement | React.ReactElement[];
    onChange?: (event: {index: number, name: string}) => void;
};

export const Tabs: React.FC<Props> = ({
    stretch, contentIndent = true, overflow = true, defaultTabName, children, onChange, className
}: Props) => {

    const items = useMemo(() => filter(Children.map(children, (child) => ({
        name: child.props.name,
        disabled: child.props.disabled,
        hidden: child.props.hidden,
        icon: child.props.icon
    })), (child) => !child.hidden), [children]);

    const defaultTabIndex = useMemo(() => {
        const index = findIndex(items, {name: defaultTabName});
        const item = items[index];
        return item && !item.disabled ? index : null;
    }, [defaultTabName, items]);

    const firstNotDisabledItemIndex = useMemo(() => findIndex(items, (item) => !item.disabled), [items]);

    const [activeTabIndex, setActiveTabIndex] = useState<number>(defaultTabIndex || firstNotDisabledItemIndex);
    const activeTab = useMemo(() => items[activeTabIndex] || items[firstNotDisabledItemIndex] || null,
        [activeTabIndex, items, firstNotDisabledItemIndex]);

    const changeActiveTab = useCallback((index: number) => {
        setActiveTabIndex(index);
        onChange?.({index, name: items[index]?.name});
    }, [items, onChange]);

    useEffect(() => {
        if (activeTabIndex !== firstNotDisabledItemIndex && (activeTab?.disabled || activeTabIndex < 0)) {
            changeActiveTab(firstNotDisabledItemIndex);
        }
    }, [activeTab, activeTabIndex, firstNotDisabledItemIndex, changeActiveTab]);

    if (isEmpty(items)) {
        return null;
    }

    return (
        <div>
            <div className={cx('tw-flex tw-font-bold tw-overflow-x-auto tw-relative', className)}>
                <div className='tw-absolute tw-w-full tw-bottom-0 tw-border-b-2 tw-border-default' />
                {items.map(({name, disabled, icon}, i) => (
                    <div
                        key={name}
                        className={cx(`tw-relative tw-p-2 tw-cursor-pointer tw-text-lg
                            tw-whitespace-nowrap tw-border-b-2`, {
                            'tw-text-primary tw-border-default': i !== activeTabIndex,
                            'tw-text-dark tw-border-dark': i === activeTabIndex,
                            'tw-text-default-dimmed tw-cursor-not-allowed': disabled,
                            'tw-flex-1 tw-text-center': stretch
                        })}
                        onClick={() => !disabled && changeActiveTab(i)}
                    >
                        {icon && <Icon className='tw-inline tw-mr-2' color={i === activeTabIndex ? 'dark' : 'primary'} name={icon} />}
                        {name}
                    </div>
                ))}
            </div>
            <div className={cx({'tw-mt-4': contentIndent, 'tw-overflow-auto': overflow})}>
                {Children.map(children, (child) =>
                    !child.props.hidden ? cloneElement(child, {activeItemName: activeTab?.name}) : null)}
            </div>
        </div>
    );
};
